import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Drawer, useTheme, type DrawerProps } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Button from "@components/Button/Button.tsx";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import { SIDE_CHILD_DRAWER_Z_INDEX } from "@constants/layout.style";

interface SideDrawerProps {
  isOpen: boolean;
  isChildDrawer?: boolean; // we use this for side drawers are are opened form an existing side drawer
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  onClose: () => void;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  /** Disables submit without showing the submit loading spinner (e.g. while a file upload is in progress) */
  isSubmitDisabled?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  children?: React.ReactNode;
  hideCancelButton?: boolean;
  keepMounted?: boolean;
  customFooter?: React.ReactNode; // Custom footer to replace default footer
  onCancel?: () => void;
  disableBackdropClose?: boolean;
  width?: string | number; // Custom width for the side drawer
}
const TEST_ID_BASE = "side-drawer";

const SideDrawer: React.FC<SideDrawerProps> = ({
  isChildDrawer = false,
  isOpen,
  title,
  description,
  onClose,
  onCancel,
  disableBackdropClose = false,
  onSubmit,
  isSubmitting,
  isSubmitDisabled = false,
  submitLabel = "Submit",
  cancelLabel = "Cancel",
  children,
  hideCancelButton = false,
  keepMounted = true,
  customFooter,
  width,
}) => {
  const theme = useTheme();
  const styles = getSideDrawerStyles(theme);

  const handleDrawerClose: NonNullable<DrawerProps["onClose"]> = (_event, reason) => {
    if (disableBackdropClose && reason === "backdropClick") return;
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleDrawerClose}
      container={globalThis.window === undefined ? undefined : () => document.body}
      ModalProps={{
        keepMounted,
        container: globalThis.window === undefined ? undefined : document.body,
        sx: { zIndex: isChildDrawer ? SIDE_CHILD_DRAWER_Z_INDEX : styles.drawer.zIndex },
      }}
      slotProps={{
        backdrop: { sx: styles.backdrop },
        paper: {
          sx: {
            ...styles.drawer,
            display: "flex",
            flexDirection: "column",
            width: width || "fit-content",
            maxWidth: "100vw",
            height: "100vh", // constrain height to viewport
          },
        },
      }}
    >
      <div className="flex flex-col h-full overflow-hidden">
        {title && (
          <div className="flex items-center justify-between p-6">
            <h6 className="font-extrabold text-xl" style={styles.headerText}>
              {title}
            </h6>
            <IconButton
              onClick={onCancel ?? onClose}
              size="small"
              className="text-gray-500"
              data-testid={`${TEST_ID_BASE}-close-icon`}
            >
              <CloseIcon />
            </IconButton>
          </div>
        )}

        {/* Scrollable Content Area */}
        <Box sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
          {description && (
            <div className="px-6">
              <p style={styles.descriptionText}>{description}</p>
            </div>
          )}
          {children}
        </Box>
        {/* Action Buttons */}
        {customFooter ? (
          customFooter
        ) : (
          <div
            className={`flex gap-3 px-6 py-6 ${hideCancelButton ? "justify-end" : ""}`}
            style={styles.footerBorder}
          >
            {!hideCancelButton && (
              <Button
                fullWidth
                label={<span className="normal-case font-medium">{cancelLabel}</span>}
                variant="text"
                onClick={onCancel ?? onClose}
                testId={`${TEST_ID_BASE}-close-button`}
              />
            )}

            <Button
              fullWidth={hideCancelButton ? false : true}
              label={<span className="normal-case font-medium">{submitLabel}</span>}
              variant="contained"
              onClick={onSubmit}
              testId={`${TEST_ID_BASE}-submit-button`}
              className={hideCancelButton ? "w-1/2 ml-auto" : ""}
              loading={isSubmitting}
              disabled={isSubmitDisabled}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default SideDrawer;
